import { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { type Nullable, throttle } from '@direct-frontend/stdlib';
import { useDrawerResize } from './lib/hooks/useDrawerResize';
import { useIsFullscreen } from './lib/hooks/useIsFullscreen';
import { useIsScrolling } from './lib/hooks/useIsScrolling';
import { useSnapComponents } from './lib/hooks/useSnapComponents';
import { useVirtualKeyboardOffset } from './lib/hooks/useVirtualKeyboardOffset';
import { type DrawerProps, type DrawerRefs, type DrawerSnap } from './lib/types';

const THROTTLE_DELAY = 100;

interface DrawerContextPayload extends Required<Pick<DrawerProps, 'onClose' | 'isFullscreen' | 'isOpen'>> {
    refs: DrawerRefs;
    snapPoint: DrawerSnap;
    scroll: {
        isDrawerScrollable: boolean;
        isScrolling: boolean;
        setScrollableNode: (node: HTMLElement) => void;
    };
}

const DrawerContext = createContext<Nullable<DrawerContextPayload>>(null);

export function useDrawerContext(): DrawerContextPayload {
    const context = useContext(DrawerContext);

    if (!context) {
        throw new Error('Hook "useDrawerContext" is called outside of DrawerContext');
    }

    return context;
}

type DrawerProviderProps = Pick<
    DrawerProps,
    'isFullscreen' | 'isSnapChildren' | 'snapPoints' | 'isOpen' | 'isScrollable' | 'children' | 'onClose'
>;

export const DrawerProvider = ({
    isOpen = false,
    isFullscreen = false,
    isSnapChildren = false,
    isScrollable = true,
    snapPoints,
    children,
    onClose,
}: DrawerProviderProps) => {
    const overlayRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const drawerRef = useRef<HTMLDivElement>(null);
    const floatingRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    const { snapPointSize, snapPointsArray, currentSnapPoint, setSnapPoint, snapResizeCallback, resetSnap } =
        useSnapComponents({
            contentRef,
            drawerRef,
            containerRef,
            isSnapChildren: Boolean(isSnapChildren && isOpen),
            snapPoints: isOpen ? snapPoints : null,
        });

    const { isDrawerFullscreen, fullscreenResizeCallback } = useIsFullscreen({
        containerRef,
        isFullscreen,
        isOpen,
        snapPointSize,
    });

    const callbackResize = useMemo(
        () =>
            throttle(() => {
                snapResizeCallback();
                fullscreenResizeCallback();
            }, THROTTLE_DELAY),
        [fullscreenResizeCallback, snapResizeCallback]
    );

    useDrawerResize({ contentRef, headerRef, containerRef, callback: callbackResize, isOpen });
    useVirtualKeyboardOffset(isOpen && !isDrawerFullscreen, drawerRef);

    const { isScrolling, setScrollableNode } = useIsScrolling({
        isOpen,
        contentRef: isScrollable ? contentRef : null,
    });

    const isDrawerScrollable = useMemo(
        () =>
            isScrollable &&
            (!snapPointsArray.length || snapPointsArray.length - 1 === currentSnapPoint || isDrawerFullscreen),
        [currentSnapPoint, isDrawerFullscreen, isScrollable, snapPointsArray.length]
    );

    const handleClose = useCallback(() => {
        onClose?.();
        resetSnap();
    }, [onClose, resetSnap]);

    const payload: DrawerContextPayload = {
        isFullscreen: isDrawerFullscreen,
        isOpen,
        snapPoint: {
            snapPointSize,
            snapPoints: snapPointsArray,
            currentSnapPoint,
            setSnapPoint,
        },
        refs: {
            overlayRef,
            drawerRef,
            containerRef,
            floatingRef,
            headerRef,
            contentRef,
        },
        scroll: {
            isDrawerScrollable,
            isScrolling,
            setScrollableNode,
        },
        onClose: handleClose,
    };

    return <DrawerContext.Provider value={payload}>{children}</DrawerContext.Provider>;
};
